import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';

import { formatDate } from 'helper/DateLib';
import { DELETED_USER_LABEL, SYSTEM_CREATOR_LABEL } from 'helper/formatUserName';
import { useDialog } from 'hooks/useDialog';
import { dialogPropsSelector } from 'store/entities/ui/slice';
import { ColoredTable } from 'components/complex/ColoredTable';
import { Checkbox, SearchInput } from 'components/simple';
import { Dialog } from 'components/complex';

const dialogId = 'export-entries-dialog';

const styles = {
  dialogPaper: ({ breakpoints }) => ({
    [breakpoints.up('xl')]: {
      maxWidth: 800,
    },
  }),
  dialogActions: {
    justifyContent: 'flex-end',
  },
  searchInput: ({ breakpoints }) => ({
    [breakpoints.up('sm')]: {
      width: '100%',
    },
  }),
  tableContainer: ({ palette }) => ({
    paddingInline: 1,
    borderRadius: 5,
    border: `1px solid ${palette.colors.darkPale}`,
  }),
  tableCell: {
    height: 60,
    paddingBlock: 2,
    '&:first-of-type': {
      borderRadius: '20px 0 0 20px',
    },
    '&:last-of-type': {
      borderRadius: '0 20px 20px 0',
    },
  },
};

const CELL_KEYS = {
  CHECKBOX: 'checkbox',
  ID: 'id',
  CREATED_BY: 'created_by',
  CREATED_DATE: 'created_date',
};

const formatHeaders = (headers = []) =>
  headers.map(({ content, ...headerData }) => ({
    ...headerData,
    cellStyles: styles.tableCell,
    content: content ? (
      <Typography variant="body2-bold" color="text.secondary" component="span">
        {content}
      </Typography>
    ) : (
      content
    ),
  }));

export const ExportEntriesDialog = () => {
  const { closeDialog } = useDialog();
  const { entries, onConfirm, isLoading } = useSelector(dialogPropsSelector);
  const [searchValue, setSearchValue] = useState('');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const prevIsLoadingRef = useRef(false);

  const filteredEntries = useMemo(() => {
    if (!searchValue) {
      return entries;
    }

    const lowerSearch = searchValue.toLowerCase();

    return entries.filter(({ created_by_details: createdByDetails }) => {
      const fullName = createdByDetails?.full_name || '';
      const email = createdByDetails?.email || '';

      return fullName.toLowerCase().includes(lowerSearch) || email.toLowerCase().includes(lowerSearch);
    });
  }, [entries, searchValue]);

  const onSelectEntry = (newValue, entryId) => {
    setSelectedEntries((prev) => (newValue ? [...prev, entryId] : prev.filter((id) => id !== entryId)));
  };

  const handleConfirm = () => {
    onConfirm(selectedEntries);
  };

  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading) {
      closeDialog();
    }

    prevIsLoadingRef.current = isLoading;
  }, [closeDialog, isLoading]);

  const getCreatedByContent = (createdByDetails) => {
    if (!createdByDetails) {
      return SYSTEM_CREATOR_LABEL;
    }

    if (createdByDetails.email) {
      return (
        <>
          {createdByDetails.full_name || createdByDetails.email}
          {createdByDetails.full_name && (
            <>
              <br />
              {createdByDetails.email}
            </>
          )}
        </>
      );
    }

    return DELETED_USER_LABEL;
  };

  const tableHeaders = [
    {
      cellKey: CELL_KEYS.CHECKBOX,
      content: null,
      cellProps: { width: 70 },
    },
    {
      cellKey: CELL_KEYS.ID,
      content: 'ID',
      cellProps: {
        width: 70,
        align: 'center',
      },
    },
    {
      cellKey: CELL_KEYS.CREATED_BY,
      content: 'Created By',
    },
    {
      cellKey: CELL_KEYS.CREATED_DATE,
      content: 'Created Date',
      cellProps: {
        width: '30%',
        align: 'center',
      },
    },
  ];

  const tableRows = filteredEntries.map(
    ({ id, entry_number: entryNumber, created_at: createdAt, created_by_details: createdByDetails }) => ({
      id,
      cells: [
        {
          cellKey: CELL_KEYS.CHECKBOX,
          cellStyles: styles.tableCell,
          content: (
            <Checkbox
              checked={selectedEntries.includes(id)}
              onChange={(newValue) => onSelectEntry(newValue, id)}
              inputProps={{
                'aria-label': `Select entry`,
                id: `entry_${id}-checkbox`,
              }}
            />
          ),
        },
        {
          cellKey: CELL_KEYS.ID,
          cellStyles: styles.tableCell,
          content: (
            <Typography variant="body1" component="span">
              {entryNumber}
            </Typography>
          ),
          cellProps: { align: 'center' },
        },
        {
          cellKey: CELL_KEYS.CREATED_BY,
          cellStyles: styles.tableCell,
          content: (
            <Typography variant="body1" component="span">
              {getCreatedByContent(createdByDetails)}
            </Typography>
          ),
        },
        {
          cellKey: CELL_KEYS.CREATED_DATE,
          cellStyles: styles.tableCell,
          content: (
            <Typography variant="body1" component="span">
              {formatDate(createdAt, 'DD.MM.YYYY')}
            </Typography>
          ),
          cellProps: { align: 'center' },
        },
      ],
    }),
  );

  return (
    <Dialog
      id={dialogId}
      title="Export Entries"
      confirmLabel="Export"
      isDisabledConfirm={!selectedEntries.length}
      isLoading={isLoading}
      onConfirm={handleConfirm}
      customStyles={{
        paperStyles: styles.dialogPaper,
        actionsStyles: styles.dialogActions,
      }}
    >
      <Stack spacing={4} alignItems="flex-start">
        <Typography variant="body1">Select entries to export as PDF</Typography>

        <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} customStyles={styles.searchInput} />

        <Box sx={styles.tableContainer}>
          <ColoredTable header={formatHeaders(tableHeaders)} rows={tableRows} />
        </Box>
      </Stack>
    </Dialog>
  );
};
